import { VectorTile } from '@mapbox/vector-tile';
import Pbf from 'pbf';
import { Auth } from 'aws-amplify';
import { API_GW_URL } from '../constants/variables';

/**
 * Прямая загрузка MVT-тайлов слоя «Треки исполнителей» по bbox смены (DA-14933).
 *
 * querySourceFeatures отдаёт только тайлы под вьюпортом, поэтому части длинной смены
 * за экраном теряются. Здесь мы вычисляем z12-тайлы, покрывающие bbox смены, тянем их
 * напрямую (минуя карту), декодируем pbf и возвращаем фрагменты геометрии по сегментам —
 * независимо от вьюпорта и зума. Авторизация — как в transformMapRequests: заголовок
 * Authorization (idToken Cognito) + query-параметры userId и date.
 */

const SOURCE_LAYER = 'martin.scr_tracks_mvt';
const TILEJSON_URL = `${API_GW_URL}/rpc/martin.scr_tracks_mvt.json`;
const DETAIL_Z = 12;        // detail-ветка MVT доступна с z12
const MAX_TILES = 64;       // предохранитель от взрыва числа запросов

let tilesTemplateCache = null;

const lon2tile = (lon, z) => Math.floor(((lon + 180) / 360) * (2 ** z));
const lat2tile = (lat, z) => {
    const r = (lat * Math.PI) / 180;
    return Math.floor(((1 - (Math.log(Math.tan(r) + (1 / Math.cos(r))) / Math.PI)) / 2) * (2 ** z));
};
const getAuth = async () => {
    const s = await Auth.currentSession();
    const idt = s.getIdToken();
    return { idToken: idt.getJwtToken(), userId: idt.payload.sub };
};
// Шаблон tiles[0] из TileJSON источника (кешируется на сессию).
const getTilesTemplate = async (idToken, userId, date) => {
    if (tilesTemplateCache) return tilesTemplateCache;
    const url = new URL(TILEJSON_URL);
    url.searchParams.set('userId', userId);
    if (date) url.searchParams.set('date', date);
    const res = await fetch(url.toString(), { headers: { Authorization: idToken } });
    if (!res.ok) return null;
    const json = await res.json();
    tilesTemplateCache = (json.tiles && json.tiles[0]) || null;
    return tilesTemplateCache;
};
const fragmentsFromGeometry = geom => {
    if (!geom) return [];
    if (geom.type === 'LineString') return geom.coordinates.length >= 2 ? [ geom.coordinates ] : [];
    if (geom.type === 'MultiLineString') return geom.coordinates.filter(c => c.length >= 2);
    return [];
};

/**
 * Тянет тайлы по bbox смены и возвращает [{ props, fragments }] фич нужной смены.
 * @param {[[number,number],[number,number]]} bbox — [[xmin,ymin],[xmax,ymax]] в 4326
 * @param {number|string} shiftId
 * @param {string} date — YYYY-MM-DD
 * @returns {Promise<Array<{props:object, fragments:Array<Array<[number,number]>>}>>}
 */
export const fetchShiftTileFeatures = async (bbox, shiftId, date) => {
    const z = DETAIL_Z;
    let auth;
    try {
        auth = await getAuth();
    } catch (e) {
        return [];
    }
    const { idToken, userId } = auth;
    const template = await getTilesTemplate(idToken, userId, date);
    if (!template) return [];

    const [ [ xmin, ymin ], [ xmax, ymax ] ] = bbox;
    const xFrom = lon2tile(xmin, z);
    const xTo = lon2tile(xmax, z);
    const yFrom = lat2tile(ymax, z); // широта инвертируется (север — меньший y)
    const yTo = lat2tile(ymin, z);
    const tiles = [];
    for (let x = xFrom; x <= xTo; x++) {
        for (let y = yFrom; y <= yTo; y++) tiles.push({ x, y });
    }
    if (tiles.length > MAX_TILES) {
        // eslint-disable-next-line no-console
        console.warn(`scrTrackTiles: bbox смены даёт ${tiles.length} тайлов на z${z}, ограничиваем до ${MAX_TILES}`);
        tiles.length = MAX_TILES;
    }

    const out = [];
    await Promise.all(tiles.map(async ({ x, y }) => {
        const url = new URL(template.replace('{z}', z).replace('{x}', x).replace('{y}', y));
        url.searchParams.set('userId', userId);
        if (date) url.searchParams.set('date', date);
        let buf;
        try {
            const res = await fetch(url.toString(), { headers: { Authorization: idToken } });
            if (!res.ok) return;
            buf = await res.arrayBuffer();
        } catch (e) {
            return;
        }
        if (!buf || buf.byteLength === 0) return;
        let layer;
        try {
            layer = new VectorTile(new Pbf(buf)).layers[SOURCE_LAYER];
        } catch (e) {
            return;
        }
        if (!layer) return;
        for (let i = 0; i < layer.length; i++) {
            const feat = layer.feature(i);
            if (feat.properties.shift_id !== shiftId) continue;
            const gj = feat.toGeoJSON(x, y, z); // tile-local → lng/lat
            const fragments = fragmentsFromGeometry(gj.geometry);
            if (fragments.length) out.push({ props: feat.properties, fragments });
        }
    }));
    return out;
};
